import { db } from './db/schema';
import type { BackupFile } from './backupSerialize';

/**
 * ============================================================================
 * BACKUP RESTORE
 * ============================================================================
 *
 * Restores a previously-validated BackupFile, REPLACING all local data —
 * this is not a merge. Callers MUST run validateBackupFile() first (see
 * backupSerialize.ts) and obtain explicit user confirmation before
 * calling this function; restoreBackup() itself does not re-validate or
 * ask for confirmation, since those are separate concerns handled by the
 * UI layer (see RecordsPage.tsx's Backup & Restore section).
 *
 * ATOMICITY: every table this touches (all 6 event tables + loans) is
 * wrapped in a single Dexie `db.transaction('rw', [tables], ...)` call.
 * Dexie's TypeScript typings only provide overloads for up to 3 tables
 * listed as individual positional arguments; beyond that, the tables
 * must be passed as a single array (also a documented, supported form
 * of the same API) — hence the array here rather than 7 separate
 * arguments. Per Dexie's documented behaviour, every table that will be
 * written must be included, and if ANY write inside the callback
 * throws, the entire transaction is rolled back automatically — none of
 * the clears or writes take effect. This means a failure partway through
 * (a malformed row that slipped past validation, a storage-quota error,
 * etc.) leaves the existing local data completely untouched rather than
 * partially wiped. This is the specific guarantee Stage 2 requires:
 * local data is never left in a half-restored state.
 *
 * IDs are preserved exactly via bulkPut/put (not bulkAdd, which would
 * throw on a primary-key collision against a UUID that already exists —
 * irrelevant here since every table is cleared first anyway, but bulkPut
 * is also simply the correct choice for "write these exact rows with
 * their original identities", which is what a faithful restore means).
 */
export async function restoreBackup(backup: BackupFile): Promise<void> {
  await db.transaction(
    'rw',
    [
      db.loans,
      db.disbursementEvents,
      db.rateChangeEvents,
      db.prepaymentEvents,
      db.balanceCorrectionEvents,
      db.chargeEvents,
      db.emiDateChangeEvents,
    ],
    async () => {
      // Replace, don't merge: clear every table before writing the
      // backup's rows back in.
      await db.loans.clear();
      await db.disbursementEvents.clear();
      await db.rateChangeEvents.clear();
      await db.prepaymentEvents.clear();
      await db.balanceCorrectionEvents.clear();
      await db.chargeEvents.clear();
      await db.emiDateChangeEvents.clear();

      if (backup.loan) {
        await db.loans.put(backup.loan);
      }
      await db.disbursementEvents.bulkPut(backup.events.disbursement);
      await db.rateChangeEvents.bulkPut(backup.events.rate_change);
      await db.prepaymentEvents.bulkPut(backup.events.prepayment);
      await db.balanceCorrectionEvents.bulkPut(backup.events.balance_correction);
      await db.chargeEvents.bulkPut(backup.events.charge);
      await db.emiDateChangeEvents.bulkPut(backup.events.emi_date_change);
    }
  );
}
